import { FC, memo, ReactNode, useRef } from 'react';
import { SPACE_FOLDER_PAGE_SIZE, SpaceNodeKind, SpaceNodeSortBy } from 'shared';
import { twMerge } from 'tailwind-merge';

import {
  DownloadFileIcon,
  EditIcon,
  EyeIconSmall,
  FolderDownloadIcon,
  TrashBinIcon,
} from '../../../../assets/icons/index';
import { Actions } from '../../../../components/actions/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { useTableSort } from '../../../../components/table/hooks/use-table-sort.hook';
import { Table, TableRow } from '../../../../components/table/index';
import { useLocale } from '../../../../hooks/index';
import { StringKey } from '../../../../lang/index';
import { useBrowserPageReveal } from '../../hooks/use-browser-page-reveal.hook';
import {
  BrowserNodeActions,
  SpaceBrowserFile,
  SpaceBrowserFolder,
  SpaceBrowserPagination,
  SpaceBrowserSort,
} from '../../types/data-room.types';
import { chunksOf } from '../../utils/chunks.util';
import { formatSpaceSize } from '../../utils/space-format.util';
import { LastActive } from '../components/last-active';
import { SpaceFolderIcon } from '../components/space-folder-icon';
import { BrowserFileIcon } from './browser-file-icon';
import { BrowserFileType } from './browser-file-type';
import {
  BrowserLoadingRows,
  BrowserPagePanel,
  BrowserPaginationBar,
  PAGE_ROW_CLASS,
} from './workspace-browser-pagination';

enum BrowserColumn {
  LAST_EDIT = 'lastEdit',
  MENU = 'menu',
  NAME = 'name',
  SIZE = 'size',
  TYPE = 'type',
}

const COLUMN_ORDER = [
  BrowserColumn.NAME,
  BrowserColumn.TYPE,
  BrowserColumn.LAST_EDIT,
  BrowserColumn.SIZE,
  BrowserColumn.MENU,
];

const ROW_CONTENT_CLASS = {
  [BrowserColumn.LAST_EDIT]: 'gap-2 px-4',
  [BrowserColumn.MENU]: 'justify-center px-0',
  [BrowserColumn.NAME]: 'min-w-0 gap-2 px-4',
  [BrowserColumn.SIZE]: 'gap-2 px-4',
  [BrowserColumn.TYPE]: 'gap-2 px-4',
};

// The name takes what the fixed columns leave, so a long one is cut rather than widening the table.
const ROW_CELL_CLASS = { [BrowserColumn.NAME]: 'min-w-0' };

const ROW_LABEL_CLASS = {
  general: 'text-sm leading-5',
  [BrowserColumn.NAME]: 'min-w-0',
  [BrowserColumn.SIZE]: 'font-normal',
  [BrowserColumn.TYPE]: 'font-normal',
};

const COLUMN_TRANSLATION_KEY: Record<BrowserColumn, StringKey> = {
  [BrowserColumn.LAST_EDIT]: 'LAST_EDIT',
  [BrowserColumn.MENU]: 'MORE_ACTIONS',
  [BrowserColumn.NAME]: 'NAME',
  [BrowserColumn.SIZE]: 'SIZE',
  [BrowserColumn.TYPE]: 'TYPE',
};

const COLUMN_BY_SORT: Record<SpaceNodeSortBy, BrowserColumn> = {
  [SpaceNodeSortBy.LAST_EDIT]: BrowserColumn.LAST_EDIT,
  [SpaceNodeSortBy.NAME]: BrowserColumn.NAME,
  [SpaceNodeSortBy.SIZE]: BrowserColumn.SIZE,
};

const SORT_BY_COLUMN: Partial<Record<BrowserColumn, SpaceNodeSortBy>> = {
  [BrowserColumn.LAST_EDIT]: SpaceNodeSortBy.LAST_EDIT,
  [BrowserColumn.NAME]: SpaceNodeSortBy.NAME,
  [BrowserColumn.SIZE]: SpaceNodeSortBy.SIZE,
};

const MENU_ITEM_CLASS = 'h-9 rounded pr-8 font-normal hover:bg-brand-25';

const MAX_NEXT_PAGE_LOADING_ROWS = 3;

const MAX_UPLOADING_ROWS = 5;

type BrowserRowActionsProps = {
  // Why rename and delete are refused for this row; they stay in the menu, inactive, saying so.
  editLockReason?: StringKey;
  // A file downloads as itself, a folder as a zip: each shows its own icon.
  kind: SpaceNodeKind;
  onDelete?: () => void;
  onDownload?: () => void;
  onRename?: () => void;
  onView?: () => void;
};

// An auditor sees the menu without the writes, as everywhere else in the app.
const WRITE_ACTION_ATTRIBUTES = { 'data-permission-required': true };

// What a row can do where its space allows it; a missing handler leaves that item out. With none,
// the row has no menu at all. A folder downloads as a zip of everything under it, a file in its own
// format.
const BrowserRowActions: FC<BrowserRowActionsProps> = ({
  editLockReason,
  kind,
  onDelete,
  onDownload,
  onRename,
  onView,
}) => {
  const { messagesLocale } = useLocale();

  if (!onDelete && !onDownload && !onRename && !onView) return null;

  const isLocked = !!editLockReason;
  const lock = isLocked
    ? { disabled: true, tooltipContent: <AppFormattedMessage id={editLockReason} /> }
    : {};

  return (
    <div onClick={(event) => event.stopPropagation()}>
      <Actions
        actions={[
          onView
            ? {
                callback: onView,
                icon: <EyeIconSmall iconColor="#344054" />,
                title: messagesLocale['PREVIEW'],
                wrapperClassName: MENU_ITEM_CLASS,
              }
            : null,
          onDownload
            ? {
                callback: onDownload,
                icon:
                  kind === SpaceNodeKind.FILE ? (
                    <DownloadFileIcon iconColor="#344054" />
                  ) : (
                    <FolderDownloadIcon className="size-4" height={16} width={16} />
                  ),
                title: messagesLocale['DOWNLOAD'],
                wrapperClassName: MENU_ITEM_CLASS,
              }
            : null,
          onRename
            ? {
                ...lock,
                attributes: WRITE_ACTION_ATTRIBUTES,
                callback: onRename,
                icon: <EditIcon iconColor={isLocked ? '#D0D5DD' : '#344054'} />,
                title: messagesLocale['RENAME'],
                wrapperClassName: MENU_ITEM_CLASS,
              }
            : null,
          onDelete
            ? {
                ...lock,
                attributes: WRITE_ACTION_ATTRIBUTES,
                callback: onDelete,
                className: isLocked ? 'font-normal text-gray-300' : 'font-normal text-fireside-500',
                icon: <TrashBinIcon iconColor={isLocked ? '#D0D5DD' : '#F04438'} />,
                title: messagesLocale['DELETE'],
                wrapperClassName: MENU_ITEM_CLASS,
              }
            : null,
        ]}
        contentClassName="w-[168px] p-2"
      />
    </div>
  );
};

type BrowserItem =
  | { kind: SpaceNodeKind.FILE; row: SpaceBrowserFile }
  | { kind: SpaceNodeKind.FOLDER; row: SpaceBrowserFolder };

export type WorkspaceFilesTableProps = {
  actions: BrowserNodeActions;
  files: SpaceBrowserFile[];
  folders: SpaceBrowserFolder[];
  onOpenFolder: (folderId: string) => void;
  onSortChange: (sort: null | SpaceBrowserSort) => void;
  pagination: SpaceBrowserPagination;
  // The rows come in this order already: a folder is paged, so only the server can sort it whole.
  sort: null | SpaceBrowserSort;
  // The columns this space's server can order by.
  sortableBy: SpaceNodeSortBy[];
  // Files uploading into this very folder, shown as placeholder rows on top until they are listed.
  uploadingCount: number;
};

const WorkspaceFilesTableView: FC<WorkspaceFilesTableProps> = ({
  actions: {
    canOpenFile,
    onDeleteFile,
    onDeleteFolder,
    onDownloadFile,
    onDownloadFolder,
    onOpenFile,
    onRenameFile,
    onRenameFolder,
  },
  files,
  folders,
  onOpenFolder,
  onSortChange,
  pagination,
  sort,
  sortableBy,
  uploadingCount,
}) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const { barRef, isFolding, onPagesFolded, showLess } = useBrowserPageReveal({
    pagination,
    rowCount: folders.length + files.length,
    tableRef,
  });

  // The header starts from the owner's order, so a table remounted for another folder keeps it.
  const [columnsTitle, , ariaSort] = useTableSort<BrowserColumn>(
    sortableBy.map((by) => COLUMN_BY_SORT[by]),
    COLUMN_TRANSLATION_KEY,
    ({ field, order }) => {
      const by = field && SORT_BY_COLUMN[field];

      onSortChange(by && order ? { by, order } : null);
    },
    sort ? { field: COLUMN_BY_SORT[sort.by], order: sort.order } : { field: null, order: null },
  );

  const nameCell = (icon: ReactNode, title: string, subtitle?: ReactNode) => (
    <span className="flex min-w-0 items-center gap-2">
      {icon}
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-[450] leading-5 text-gray-700" title={title}>
          {title}
        </span>
        {subtitle ? (
          <span className="text-label-sm font-medium text-gray-500">{subtitle}</span>
        ) : null}
      </span>
    </span>
  );

  // A file opens in a tab of its own where the space can show it. One a browser cannot show is saved
  // instead, and still counts as the preview it was asked for.
  const opener = (file: SpaceBrowserFile) =>
    onOpenFile && (canOpenFile?.(file) ?? true) ? () => onOpenFile(file.id) : undefined;

  const renderRow = ({ kind, row }: BrowserItem, className?: string) =>
    kind === SpaceNodeKind.FOLDER ? (
      <TableRow
        columnOrder={COLUMN_ORDER}
        contentClassName={ROW_CONTENT_CLASS}
        fields={{
          id: row.id,
          [BrowserColumn.NAME]: nameCell(
            <SpaceFolderIcon className="size-8" isShared={row.isShared} size={32} />,
            row.name,
            row.itemCount === null ? undefined : (
              <AppFormattedMessage id="ITEMS_COUNT" values={{ count: row.itemCount }} />
            ),
          ),
          [BrowserColumn.TYPE]: <AppFormattedMessage id="FOLDER" />,
          [BrowserColumn.LAST_EDIT]: <LastActive at={row.updatedAt} />,
          [BrowserColumn.SIZE]: formatSpaceSize(row.size),
          [BrowserColumn.MENU]: (
            <BrowserRowActions
              kind={SpaceNodeKind.FOLDER}
              onDelete={
                onDeleteFolder
                  ? () => onDeleteFolder({ id: row.id, kind: SpaceNodeKind.FOLDER, name: row.name })
                  : undefined
              }
              onDownload={onDownloadFolder ? () => onDownloadFolder(row.id) : undefined}
              onRename={
                onRenameFolder
                  ? () => onRenameFolder({ id: row.id, kind: SpaceNodeKind.FOLDER, name: row.name })
                  : undefined
              }
            />
          ),
        }}
        key={row.id}
        labelClassName={ROW_LABEL_CLASS}
        onClick={() => onOpenFolder(row.id)}
        tdClassName={ROW_CELL_CLASS}
        trClassName={twMerge('cursor-pointer', className)}
      />
    ) : (
      <TableRow
        columnOrder={COLUMN_ORDER}
        contentClassName={ROW_CONTENT_CLASS}
        fields={{
          id: row.id,
          [BrowserColumn.NAME]: nameCell(
            <BrowserFileIcon contentType={row.contentType} />,
            row.name,
          ),
          [BrowserColumn.TYPE]: <BrowserFileType isDocusignSynced={row.isDocusignSynced} />,
          [BrowserColumn.LAST_EDIT]: <LastActive at={row.updatedAt} />,
          [BrowserColumn.SIZE]: formatSpaceSize(row.size),
          [BrowserColumn.MENU]: (
            <BrowserRowActions
              editLockReason={row.editLockReason}
              kind={SpaceNodeKind.FILE}
              onDelete={
                onDeleteFile
                  ? () => onDeleteFile({ id: row.id, kind: SpaceNodeKind.FILE, name: row.name })
                  : undefined
              }
              onDownload={onDownloadFile ? () => onDownloadFile(row.id) : undefined}
              onRename={
                onRenameFile
                  ? () => onRenameFile({ id: row.id, kind: SpaceNodeKind.FILE, name: row.name })
                  : undefined
              }
              onView={opener(row)}
            />
          ),
        }}
        key={row.id}
        labelClassName={ROW_LABEL_CLASS}
        onClick={opener(row)}
        tdClassName={ROW_CELL_CLASS}
        trClassName={twMerge(opener(row) && 'cursor-pointer', className)}
      />
    );

  // Sub-folders first, then files, in pages of the server's size: the first page stays put, each
  // later one sits in a panel that opens under it, as the cap table opens its extra rows.
  const [firstPage = [], ...laterPages] = chunksOf(
    [
      ...folders.map((row): BrowserItem => ({ kind: SpaceNodeKind.FOLDER, row })),
      ...files.map((row): BrowserItem => ({ kind: SpaceNodeKind.FILE, row })),
    ],
    SPACE_FOLDER_PAGE_SIZE,
  );

  // One list of panels keyed by page, so the panel holding placeholders for a page on its way is
  // the very one its rows fill: it stays open and the rows fade in over the placeholders.
  const pagePanels = [
    ...laterPages.map((page, index) => (
      <BrowserPagePanel
        columnCount={COLUMN_ORDER.length}
        isFolding={isFolding}
        isLoading={false}
        key={`page-${index + 1}`}
        onFolded={index === 0 ? onPagesFolded : undefined}
      >
        {page.map((item) => renderRow(item, PAGE_ROW_CLASS))}
      </BrowserPagePanel>
    )),
    ...(pagination.isLoadingMore
      ? [
          <BrowserPagePanel
            columnCount={COLUMN_ORDER.length}
            isFolding={false}
            isLoading
            key={`page-${laterPages.length + 1}`}
          >
            <BrowserLoadingRows
              count={Math.min(MAX_NEXT_PAGE_LOADING_ROWS, pagination.nextPageSize)}
            />
          </BrowserPagePanel>,
        ]
      : []),
  ];

  return (
    // shrink-0: as a flex item with overflow-hidden it would otherwise shrink to the visible area and
    // clip its rows, leaving the scroll container nothing to scroll.
    <div
      className="shrink-0 overflow-hidden rounded-lg border border-gray-100 bg-white"
      ref={tableRef}
    >
      <Table
        ariaSort={ariaSort}
        className="table-fixed [&>tbody]:divide-[#F2F4F7]"
        columnOrder={COLUMN_ORDER}
        columnsTitle={{
          ...columnsTitle,
          [BrowserColumn.MENU]: '',
        }}
        tHeadClassNames={{
          // A sortable column's title sits in a span of its own, at a lighter weight than the rest.
          labelClassName: '[&_span]:font-medium',
          thClassName: {
            general: 'px-4',
            [BrowserColumn.TYPE]: 'w-[109px]',
            [BrowserColumn.LAST_EDIT]: 'w-[127px]',
            [BrowserColumn.SIZE]: 'w-[97px]',
            [BrowserColumn.MENU]: 'w-12',
          },
        }}
      >
        <BrowserLoadingRows count={Math.min(MAX_UPLOADING_ROWS, uploadingCount)} />
        {firstPage.map((item) => renderRow(item))}
        {pagePanels}
      </Table>
      {(pagination.nextPageSize > 0 || pagination.canShowLess || isFolding) && (
        <BrowserPaginationBar
          count={pagination.nextPageSize}
          isLoading={pagination.isLoadingMore || pagination.isReloading}
          onLoadMore={pagination.loadMore}
          onShowLess={() => void showLess()}
          ref={barRef}
        />
      )}
    </div>
  );
};

// Every row re-renders otherwise whenever the page does, a drag passing over it included.
export const WorkspaceFilesTable = memo(WorkspaceFilesTableView);
